import {
  IonBackButton,
  IonButton,
  IonButtons,
  IonContent,
  IonHeader,
  IonIcon,
  IonPage,
  IonToolbar,
  useIonRouter,
} from "@ionic/react";
import { useTranslation } from "react-i18next";
import { useLocation, useParams } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { shareOutline } from "ionicons/icons";
import { toast } from "sonner";
import { api } from "@/lib/backend";
import { extractionWrapper } from "@/lib/backend/utils/extract";
import { Share } from "@/lib/capacitor/share";
import { queryOptions_signed_key_project } from "@/lib/requests/file/file.get-signed-key";
import { Routes } from "@/lib/router";
import { cn } from "@/lib/style/cva.config";
import { logger } from "@/logger";
import { useIonicQueryParam } from "@/utils/hooks/use-ionic-query-param";

interface ProjectLayoutProps {
  children: React.ReactNode;
}

function TabButton({
  children,
  isActive,
  onClick,
  // Use on click because setting replace is otherwhise not possible
}: {
  children: React.ReactNode;
  isActive: boolean;
  onClick?: () => void;
}) {
  return (
    <IonButton
      fill="clear"
      onClick={onClick}
      className={cn(
        "m-0 border-b-2 p-2 text-sm text-txt-secondary",
        isActive && "border-brand-500 font-medium text-brand-500",
        !isActive && "border-transparent",
      )}
    >
      {children}
    </IonButton>
  );
}

export function ProjectLayout({ children }: ProjectLayoutProps) {
  const { t } = useTranslation(["custom/project-detail"]);
  const { id } = useParams<{ id: string }>();
  const locationObj = useLocation();
  const location = "action" in locationObj ? locationObj.location : locationObj;
  const router = useIonRouter();
  const fromUrl = useIonicQueryParam("fromUrl");
  const isTabActive = (path: string) => {
    return location.pathname === path;
  };

  const { data: project } = useQuery({
    queryKey: ["project", id],
    queryFn: async () => {
      const res = await api.project.get(id);
      return res.data;
    },
  });

  const imageQueryOptions = queryOptions_signed_key_project([id, project?.image ?? ""]);
  const { data: imageData } = useQuery({
    ...imageQueryOptions,
    enabled: !!project?.image,
  });

  const { data: organization } = useQuery({
    queryKey: ["organization"],
    queryFn: () => {
      return extractionWrapper(api.organization.getMyOrganization());
    },
  });
  return (
    <IonPage>
      <IonContent fullscreen>
        {/* Hero Image section */}
        <div className="sticky top-0 z-10">
          <div className="relative h-64 w-full">
            {imageData?.url ? (
              <img src={imageData.url} alt={project?.name} className="h-full w-full object-cover" />
            ) : (
              <div className="h-full w-full bg-brand-500" />
            )}
            {/* Gradient overlay */}
            <div
              className="absolute inset-0"
              style={{
                background:
                  "linear-gradient(to bottom, rgba(0,0,0,0), rgba(0,0,0,0.5)), linear-gradient(to bottom, rgba(var(--config-color-brand-600-rgb), 0), rgba(var(--config-color-brand-600-rgb), 0.4))",
              }}
            />
            {/* Header */}
            <IonHeader className="ion-no-border absolute left-0 right-0 top-0">
              <IonToolbar className="[--ion-toolbar-background-color:transparent] [--ion-toolbar-background:transparent]">
                <IonButtons slot="start" className="mt-4">
                  <IonBackButton
                    className="text-white"
                    defaultHref={fromUrl ?? Routes.Projects()}
                  />
                </IonButtons>
                <IonButtons slot="end" className="mt-4 text-white">
                  <IonButton
                    className="text-white"
                    onClick={async () => {
                      try {
                        const canShareResult = await Share.canShare();
                        if (canShareResult) {
                          await Share.share({
                            title: project?.name || "Project",
                            text: `Check out this project: ${project?.name}`,
                            url: `${import.meta.env.VITE_BACKEND_URL}/deeplink/${organization?.slug}/app/projects/${id}`,
                          });
                        } else {
                          toast.error("Share is not supported on this device");
                        }
                      } catch (err) {
                        logger.error("Share failed:", err);
                      }
                    }}
                  >
                    <IonIcon className="text-white" slot="icon-only" icon={shareOutline} />
                  </IonButton>
                </IonButtons>
              </IonToolbar>
            </IonHeader>
            {/* Project title */}
            <h1 className="absolute bottom-4 left-4 text-2xl font-bold text-white">
              {project?.name}
            </h1>
          </div>
          {/* Tabs navigation */}
          <div className="border-b border-gray-200 bg-white px-4">
            <nav className="flex justify-between">
              <TabButton
                onClick={() => {
                  router.push(
                    Routes.ProjectOverview({ id }, { search: { fromUrl: fromUrl ?? "" } }),
                    "none",
                    "replace",
                  );
                }}
                isActive={isTabActive(Routes.ProjectOverview({ id }))}
              >
                {t("routes:projects.overview.title")}
              </TabButton>
              <TabButton
                onClick={() => {
                  router.push(
                    Routes.ProjectInfo({ id }, { search: { fromUrl: fromUrl ?? "" } }),
                    "none",
                    "replace",
                  );
                }}
                isActive={isTabActive(Routes.ProjectInfo({ id }))}
              >
                {t("routes:projects.info.title")}
              </TabButton>
              <TabButton
                onClick={() => {
                  router.push(
                    Routes.ProjectShifts({ id }, { search: { fromUrl: fromUrl ?? "" } }),
                    "none",
                    "replace",
                  );
                }}
                isActive={isTabActive(Routes.ProjectShifts({ id }))}
              >
                {t("routes:projects.shifts.title")}
              </TabButton>
            </nav>
          </div>
        </div>

        {/* Tab content */}
        <div className="px-4">{children}</div>
      </IonContent>
    </IonPage>
  );
}
